'use client';

import { CardAppointmentRealizedByType } from './card-appointment-realized-by-type.component';
import { CardAppointmentScheduled } from './variants/card-appointment-scheduled.component';
import { CardAppointmentFlex } from './variants/card-appointment-flex.component';
import { CardAppointmentArrivalOrder } from './variants/card-appointment-arrival-order.component';
import { CardAppointmentTelemed } from './variants/card-appointment-telemed.component';
import { CardAppointmentStandalone } from './variants/card-appointment-standalone.component';
import { CardAppointmentLab } from './variants/card-appointment-lab.component';

import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Appointment } from '@/types/appointments.types';
import { AppointmentMode, AppointmentsLifecycleEnum, AppointmentType } from '@/types/enums/appointments.enum';
import { dateFormatter } from '@/utils/formatters/date';

export interface CardAppointmentProps {
  appointment: Appointment;
  defaultOpen?: boolean;
  onAppointmentUpdate: (appointment: Partial<Appointment>) => void;
}

interface CardAppointmentByLifecycleProps extends CardAppointmentProps {
  lifecycle: AppointmentsLifecycleEnum;
}

export default function CardAppointment({
  appointment,
  lifecycle,
  defaultOpen = true,
  onAppointmentUpdate,
}: CardAppointmentByLifecycleProps) {
  const isUpcomingFromAnEarlierDay
    = lifecycle === AppointmentsLifecycleEnum.FUTURE
      && appointment.hasFinished
      && Boolean(appointment.date)
      && new Date(appointment.date) < new Date()
      && dateFormatter(appointment.date, 'date') !== dateFormatter(new Date(), 'date');

  if (lifecycle === AppointmentsLifecycleEnum.PAST || isUpcomingFromAnEarlierDay) {
    return <CardAppointmentRealizedByType appointment={appointment} defaultOpen={defaultOpen} />;
  }

  if (appointment.mode === AppointmentMode.STANDALONE) {
    return (
      <CardAppointmentStandalone
        appointment={appointment}
        defaultOpen={defaultOpen}
        onAppointmentUpdate={onAppointmentUpdate}
      />
    );
  }

  if (appointment.type === AppointmentType.LABORATORIAL) {
    return (
      <CardAppointmentLab
        appointment={appointment}
        defaultOpen={defaultOpen}
        onAppointmentUpdate={onAppointmentUpdate}
      />
    );
  }

  if (appointment.isTelehealth) {
    return (
      <CardAppointmentTelemed
        appointment={appointment}
        defaultOpen={defaultOpen}
        onAppointmentUpdate={onAppointmentUpdate}
      />
    );
  }

  if (appointment.scheduleType === ScheduleType.ARRIVAL_ORDER) {
    return (
      <CardAppointmentArrivalOrder
        appointment={appointment}
        defaultOpen={defaultOpen}
        onAppointmentUpdate={onAppointmentUpdate}
      />
    );
  }

  if (appointment.scheduleType === ScheduleType.FLEX && !appointment.date) {
    return (
      <CardAppointmentFlex
        appointment={appointment}
        defaultOpen={defaultOpen}
        onAppointmentUpdate={onAppointmentUpdate}
      />
    );
  }

  return (
    <CardAppointmentScheduled
      appointment={appointment}
      defaultOpen={defaultOpen}
      onAppointmentUpdate={onAppointmentUpdate}
    />
  );
}
